import React from 'react';

import type {
  ChartV2LegendMoreItem,
  ChartV2Series,
  ChartV2SeriesType,
} from '../types/chart-v2.types';
import { ChartTypeSelector } from './ChartTypeSelector';
import { ColorPalette } from './ColorPalette';
import {
  AreaIcon,
  ArrowDownIcon,
  ArrowUpIcon,
  BarIcon,
  CandleIcon,
  LineIcon,
} from './Icons';
import styles from './Legend.module.scss';

const seriesTypeIcon: Record<ChartV2SeriesType, React.ReactNode> = {
  line: <LineIcon />,
  area: <AreaIcon />,
  candlestick: <CandleIcon />,
  bar: <BarIcon />,
};

const DEFAULT_SERIES_TYPE_LABEL: Record<ChartV2SeriesType, string> = {
  line: 'Line chart',
  area: 'Area chart',
  candlestick: 'Candlestick chart',
  bar: 'Bar chart',
};

export interface BuildDefaultLegendMenuLabels {
  changeColor?: string;
  moveUp?: string;
  moveDown?: string;
  seriesTypeLabel?: Partial<Record<ChartV2SeriesType, string>>;
}

export interface BuildDefaultLegendMenuArgs {
  seriesId: string;
  color: string;
  seriesType: ChartV2SeriesType;
  hasOhlc: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  colors: string[];
  onColorChange: (color: string, colorIndex: number) => void;
  onSeriesTypeChange: (seriesType: ChartV2SeriesType) => void;
  onMovePane?: (direction: 'up' | 'down') => void;
  labels?: BuildDefaultLegendMenuLabels;
}

export const buildDefaultLegendMenu = ({
  seriesType,
  color,
  hasOhlc,
  canMoveUp,
  canMoveDown,
  colors,
  onColorChange,
  onSeriesTypeChange,
  onMovePane,
  labels,
}: BuildDefaultLegendMenuArgs): ChartV2LegendMoreItem[] => {
  const disabledTypes: ChartV2SeriesType[] = hasOhlc
    ? []
    : ['candlestick', 'bar'];

  const resolvedSeriesTypeLabel =
    labels?.seriesTypeLabel?.[seriesType] ??
    DEFAULT_SERIES_TYPE_LABEL[seriesType];

  return [
    {
      id: 'change-color',
      label: labels?.changeColor ?? 'Change chart color',
      leadingIcon: (
        <div className={styles.colorDot} style={{ backgroundColor: color }} />
      ),
      submenu: (
        <ColorPalette
          colors={colors}
          selectedColor={color}
          onSelect={onColorChange}
        />
      ),
    },
    {
      id: 'chart-type',
      label: resolvedSeriesTypeLabel,
      leadingIcon: seriesTypeIcon[seriesType],
      submenu: (
        <ChartTypeSelector
          selected={seriesType}
          disabledTypes={disabledTypes}
          onChange={onSeriesTypeChange}
        />
      ),
    },
    // v1 parity: a move the pane layout can't perform is left out of the
    // menu entirely rather than rendered greyed-out.
    ...(canMoveUp
      ? [
          {
            id: 'move-up',
            label: labels?.moveUp ?? 'Move up',
            leadingIcon: <ArrowUpIcon />,
            onClick: () => onMovePane?.('up'),
          },
        ]
      : []),
    ...(canMoveDown
      ? [
          {
            id: 'move-down',
            label: labels?.moveDown ?? 'Move down',
            leadingIcon: <ArrowDownIcon />,
            onClick: () => onMovePane?.('down'),
          },
        ]
      : []),
  ];
};

export const seriesHasOhlc = (series: ChartV2Series): boolean =>
  series.data.some(p => 'close' in p);
